import {
  type ComponentType,
  type ElementType,
  type Ref,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';
import { type Lens, useLens } from '@hookform/lenses';
import { type DefaultValues, FormProvider, type Path, type Resolver, type SubmitHandler, useForm } from 'react-hook-form';
import type { ZodError, ZodType } from 'zod/v4';
import { useShallow } from 'zustand/shallow';
import { Button, type ButtonProps } from '@interxfi/design-system/components/button/button';
import type { ButtonState } from '@interxfi/design-system/components/button/shared';
import { DrawerButtonRow, DrawerContent } from '@interxfi/design-system/components/dialog/drawer/shared';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import {
  useFormWizardContext,
  useFormWizardMarkStepDirty,
  useFormWizardSetStepValue,
  useFormWizardStepIsDirty,
  useFormWizardSteps,
} from '../form-wizard-context';
import { FormWizardFormStepContextProvider } from './form-wizard-form-step-context';
import { FormWizardSeedContextProvider } from './form-wizard-seed-context';
import type { BaseFormWizardStepGeneric, FormWizardBaseStep } from './form-wizard-step';

export interface FormWizardStepFormRenderProps<TSteps extends BaseFormWizardStepGeneric, TKey extends keyof TSteps> {
  isValid?: boolean;
  isDirty?: boolean;
  lens: Lens<TSteps[TKey]>;
}

const DEFAULT_ON_SUBMIT_ERROR_MESSAGE = 'There is a problem with the data you provided, please try again.';

function getOnSubmitErrorMessage<TData>(error: ZodError<TData>) {
  const messages = new Set<string>();

  for (const issue of error.issues) {
    messages.add(issue.message);
  }

  if (!messages.size) {
    messages.add(DEFAULT_ON_SUBMIT_ERROR_MESSAGE);
  }

  return Array.from(messages).join('\n');
}

export type ExtendedFormWizardStepContext<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined> = TContext & {
  values?: TSteps;
};

export interface FormWizardReducerOutput<TSteps extends BaseFormWizardStepGeneric, TContext extends object | undefined = {}> {
  steps: TSteps;
  context: TContext | undefined;
}

export interface FormWizardFormStep<
  TSteps extends BaseFormWizardStepGeneric,
  TKey extends keyof TSteps,
  TContext extends object | undefined = {},
  TResolver extends Resolver<TSteps[TKey], ExtendedFormWizardStepContext<TSteps, TContext>, TSteps[TKey]> = Resolver<
    TSteps[TKey],
    ExtendedFormWizardStepContext<TSteps, TContext>,
    TSteps[TKey]
  >,
  TComponent extends ComponentType<FormWizardStepFormRenderProps<TSteps, TKey>> = ComponentType<FormWizardStepFormRenderProps<TSteps, TKey>>,
> extends FormWizardBaseStep {
  kind: 'form';
  children: TComponent;
  continueCta?: string;
  continueProps?: ButtonProps;
  ContinueCtaPreSlot?: ElementType<{ lens: Lens<TSteps[TKey]> }>;
  hideButtonRow?: boolean;
  id: TKey;
  onGoBackReducer?: (data: TSteps, ctx: TContext | undefined) => FormWizardReducerOutput<TSteps, TContext>;
  requireDirty?: boolean;
  schema: TResolver;
  submitSchema?: ZodType<TSteps[TKey], TSteps[TKey]>;
}

interface FormWizardFormStepProps<
  TSteps extends BaseFormWizardStepGeneric,
  TKey extends keyof TSteps,
  TContext extends object = {},
  TResolver extends Resolver<TSteps[TKey], TContext, TSteps[TKey]> = Resolver<TSteps[TKey], TContext, TSteps[TKey]>,
> extends FormWizardFormStep<TSteps, TKey, TContext, TResolver> {
  context?: TContext;
  disabled?: boolean;
  onSubmit: (data: TSteps) => void | Promise<void>;
  saveCtaRef: Ref<HTMLButtonElement> | undefined;
  saveCtaState: ButtonState | undefined;
}

export function FormWizardFormStepComponent<
  TSteps extends BaseFormWizardStepGeneric,
  TKey extends keyof TSteps,
  TContext extends object = {},
  TResolver extends Resolver<TSteps[TKey], TContext, TSteps[TKey]> = Resolver<TSteps[TKey], TContext, TSteps[TKey]>,
>({
  children: FormComponent,
  context,
  continueCta,
  continueProps,
  ContinueCtaPreSlot,
  disabled,
  hideButtonRow,
  id,
  onSubmit,
  requireDirty,
  saveCtaRef,
  saveCtaState,
  schema,
  submitSchema,
}: FormWizardFormStepProps<TSteps, TKey, TContext, TResolver>) {
  const addToast = useAddToast();
  const [formRef, setFormRef] = useState<HTMLFormElement | null>(null);
  const markStepDirty = useFormWizardMarkStepDirty<TSteps, TContext>();
  const stepHasBeenDirty = useFormWizardStepIsDirty<TSteps, TContext>(id);
  const setValue = useFormWizardSetStepValue<TSteps, TContext>();
  const initialValues = useFormWizardContext<TSteps, TContext, TSteps[TKey]>(useShallow((state) => state.get(id)));
  const stepData = useFormWizardSteps<TSteps, TContext>();
  const extendedContext = useMemo(() => ({ ...context, values: stepData }), [context, stepData]);
  const form = useForm<TSteps[TKey], TContext, TSteps[TKey]>({
    context: extendedContext as TContext & { values: TSteps },
    defaultValues: initialValues as DefaultValues<TSteps[TKey]>,
    mode: 'all',
    resolver: schema,
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset: resetForm,
    setError,
    trigger,
  } = form;
  const lens = useLens({ control });

  const resetAndTrigger = useEffectEvent((values: TSteps[TKey]) => {
    resetForm(values, {
      keepDirtyValues: false,
      keepErrors: false,
      keepValues: false,
      keepDirty: false,
      keepIsValid: false,
      keepTouched: true,
      keepDefaultValues: true,
    });

    void trigger();
  });

  // If the wizard's values for the form change, reset the form to those values
  useEffect(() => {
    resetAndTrigger(initialValues);
  }, [initialValues]);

  const handleFormValueSubmission: SubmitHandler<TSteps[TKey]> = useCallback(
    (values: TSteps[TKey]) => {
      if (submitSchema) {
        const { success, error } = submitSchema.safeParse(values);

        if (!success) {
          error.issues?.forEach((issue) => {
            setError(issue.path.join('.') as Path<TSteps[TKey]>, { message: issue.message }, { shouldFocus: true });
          });

          addToast({
            id: `form-wizard-step-${String(id)}-submission-error`,
            variant: 'error',
            content: getOnSubmitErrorMessage(error),
          });

          return;
        }
      }

      onSubmit(setValue(id, values));
    },
    [id, setValue, onSubmit, submitSchema, setError, addToast],
  );

  const handleFormSubmit = useCallback(
    (e: SyntheticEvent<HTMLFormElement>) => {
      // This is necessary when there are nested forms, to prevent the outer form from also submitting
      e.stopPropagation();
      e.preventDefault();

      handleSubmit(handleFormValueSubmission)(e);
    },
    [handleSubmit, handleFormValueSubmission],
  );

  useEffect(() => {
    if (isDirty) {
      markStepDirty(id);
    }
  }, [isDirty, id, markStepDirty]);

  const usableIsDirty = isDirty || stepHasBeenDirty;

  return (
    <FormWizardFormStepContextProvider formRef={formRef}>
      <FormProvider {...form}>
        <FormWizardSeedContextProvider<TSteps, TKey> id={id}>
          <DrawerContent as="form" onSubmit={handleFormSubmit} ref={setFormRef}>
            <FormComponent isDirty={usableIsDirty} isValid={isValid} lens={lens} />

            <DrawerButtonRow hidden={hideButtonRow} preSlot={ContinueCtaPreSlot ? <ContinueCtaPreSlot lens={lens} /> : undefined}>
              <Button
                disabled={disabled || !isValid || (!usableIsDirty && requireDirty)}
                fullWidth
                ref={saveCtaRef}
                size="large"
                state={saveCtaState}
                type="submit"
                variant="primary"
                {...continueProps}
              >
                {continueCta || 'Continue'}
              </Button>
            </DrawerButtonRow>
          </DrawerContent>
        </FormWizardSeedContextProvider>
      </FormProvider>
    </FormWizardFormStepContextProvider>
  );
}
